import { call, put, select, all } from 'redux-saga/effects';
import * as actions from './actions';
import { produce } from 'immer';
import axios from 'axios';
import { actions as TemplateActions } from '../template';
import { actions as AttachmentTemplatesActions } from '../attachmentTemplate';
import { actions as RecipientQueryTemplatesActions } from '../recipientQueryTemplate';
import { excludeProductCategories, excludeProductNames, types } from 'constants.js';
import { getEnvVar } from 'helpers';
import { sagas as attachmentTemplateSagas } from 'store/attachmentTemplate';
import { sagas as templateSagas } from 'store/template';
import { convertToDateOnlyString } from 'helpers/timeUtils';
import {
  waitFor,
  flattenExpressionForRequest,
  mapExpressionFromResposne
} from 'store/helpers';
import {
  updatePaginatedFilters
} from 'store/helpers/tableAndToolbar/actions';
import { sagas as eventSagas } from 'store/event';
import { getParams, getRandomDecimal } from 'store/helpers/helperFunctions';
import { updateCampaignFilters } from './actions';
const { waitForGetSupportedEventTypes, waitForGetEventMetaData } = eventSagas;
const { waitForGetAttachmentsTemplates } = attachmentTemplateSagas;
const { waitForGetTemplates } = templateSagas;

// helpers to reshape and hydrate data
const mapTemplateRules = (campaign, attributes = []) => {
  let templates = (campaign.communicationTemplateConditionQueries || []).map(
    rule => {
      return {
        id: getRandomDecimal(),
        communicationTemplateUuid: rule.communicationTemplateUuid,
        ...mapExpressionFromResposne(rule.conditionExpression, attributes)
      };
    }
  );

  let updatedCampaign = produce(campaign, draftCampaign => {
    if (templates.length > 0) {
      draftCampaign.templates = templates;
    }
    if (draftCampaign.hasOwnProperty('_hasSetTemplate')) {
      draftCampaign._hasSetTemplate = true;
    }
  });

  return updatedCampaign;
};

const flattenTemplateRulesForRequest = (templates = []) => {
  return templates.flatMap(template => {
    let temp = { ...template };
    delete temp.communicationTemplateUuid;
    return {
      communicationTemplateUuid: template.communicationTemplateUuid,
      conditionExpression: flattenExpressionForRequest(Object.assign({}, temp))
    };
  });
};

export function* waitForGetAllCampaigns() {
  return yield waitFor(
    actions.getCampaigns(),
    actions.GETTING_CAMPAIGNS_SUCCEEDED,
    actions.GETTING_CAMPAIGNS_FAILED
  );
}

export function* waitForGetAdvancedSearchPaginatedCampaigns(activeFilter = null) {
  // Don't pass searchScopes - let the saga auto-detect based on businessContextId
  // Use force: false to avoid refetching when campaigns are already loaded
  return yield waitFor(
    actions.getAdvancedSearchPaginatedCampaigns({ force: false, activeFilter }),
    actions.GETTING_ADVANCED_SEARCH_PAGINATED_CAMPAIGNS_SUCCEEDED,
    actions.GETTING_ADVANCED_SEARCH_PAGINATED_CAMPAIGNS_FAILED
  );
}

// the actual http calls
const getCampaignApiRoot = (tenantId, businessContextId) =>
  `${window._env_?.REACT_APP_API_PATH}tenants/${tenantId}/context/${businessContextId}/campaign`;

let isArchiveInProgress = false;

export const getCampaignsAsync = (tenantId, businessContextId, campaignUuid) =>
  axios
    .get(
      `${getCampaignApiRoot(tenantId, businessContextId)}${
        campaignUuid ? `/uuid/${campaignUuid}` : ''
      }`
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

    //deprecated
    export const getCampaignsAsyncWithId = (tenantId, businessContextId, campaignId) =>
  axios
    .get(
      `${getCampaignApiRoot(tenantId, businessContextId)}${
        campaignId ? `/${campaignId}` : ''
      }`
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const getPaginatedCampaignsAsync = (
  tenantId,
  businessContextId,
  urlParams,
  bodyParams
) => {
  return axios
    .post(
      `${getCampaignApiRoot(tenantId, businessContextId)}/search?index=${
        urlParams.index
      }&pageSize=${urlParams.pageSize}`,
      bodyParams
    )
    .then(response => ({
      data: response.data,
      totalRecords: parseInt(response.headers['x-ag-totalcount']) || 0
    }))
    .catch(err => {
      throw err;
    });
};


export const getAdvancedSearchPaginatedCampaignsAsync = (
  tenantId,
  businessContextId,
  urlParams,
  bodyParams
) => {
  return axios
    .post(
      `${getCampaignApiRoot(tenantId, businessContextId)}/advance-search?index=${
        urlParams.index
      }&pageSize=${urlParams.pageSize}`,
      bodyParams
    )
    .then(response => ({
      data: response.data,
      totalRecords: parseInt(response.headers['x-ag-totalcount']) || 0
    }))
    .catch(err => {
      throw err;
    });
};

export const createCampaignAsync = (tenantId, businessContextId, campaign) =>
  axios
    .post(`${getCampaignApiRoot(tenantId, businessContextId)}`, campaign)
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const updateCampaignAsync = (tenantId, businessContextId, campaign) =>
  axios
    .put(
      `${getCampaignApiRoot(tenantId, businessContextId)}/uuid/${campaign.uuid}`,
      campaign
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

    //deprecated
    export const updateCampaignAsyncWithId = (tenantId, businessContextId, campaign) =>
  axios
    .put(
      `${getCampaignApiRoot(tenantId, businessContextId)}/${campaign.id}`,
      campaign
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const deleteCampaignAsync = (tenantId, businessContextId, campaignUuid) =>
  axios
    .delete(`${getCampaignApiRoot(tenantId, businessContextId)}/uuid/${campaignUuid}`)
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

    //deprecated
    export const deleteCampaignAsyncWithId = (tenantId, businessContextId, campaignId) =>
  axios
    .delete(`${getCampaignApiRoot(tenantId, businessContextId)}/${campaignId}`)
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

const getAssociatedCampaignsAsync = async (
  tenantId,
  businessContextId,
  id,
  type
) => {
  const queryParamTypeMap = {
    [types.TEMPLATE]: 'templateUuid',
    [types.ATTACHMENT_TEMPLATE]: 'attachmentUuid',
    [types.QUERY_TEMPLATE]: 'campaignRecipientQueryUuid'
  };

  let queryParamType = queryParamTypeMap[type];

  return axios
    .get(
      `${getCampaignApiRoot(
        tenantId,
        businessContextId
      )}/findActiveFor/?${queryParamType}=${id}`
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });
};

export const getAllActiveCampaignsAsync = (
  tenantId, 
  businessContextId
) => {
  return axios
    .get(`${getCampaignApiRoot(tenantId, businessContextId)}/all-active/lazy`)
    .then(response => response.data)
    .catch(err => {
      throw err;
    });
};

// sagas

// selectors
const currentTenantId = store => store.user.selectedTenant.tenantId;
const currentBusinessContextId = store =>
  store.user.selectedBusinessContext.businessContextId;

const currentCampaign = store => (store.campaign ? store.campaign.current : {});
const campaignListSelector = store =>
  store.campaign ? store.campaign.list : [];

const paginatedCampaignList = store =>
  store.campaign ? store.campaign.paginatedList : [];
const advancedPaginatedCampaignList = store =>
  store.campaign ? store.campaign.advancedPaginatedList : [];
const productNames = store => store.events.supportedEventTypes.productNames;
const productCategories = store =>
  store.events.supportedEventTypes.productCategories;
const pageNo = store =>
  store.campaign ? store.campaign.filters?.pageNo : 0;
const pageNoFilter = store =>
  store.tableAndToolbar ? store.tableAndToolbar.paginatedFilters.pageNo : 0;
const searchInput = store =>
  store.campaign.filters ? store.campaign.filters?.searchInput : '';
const searchInputFilter = store =>
  store.tableAndToolbar ? store.tableAndToolbar.paginatedFilters.searchInput : '';
const sortBy = store =>
  store.campaign.filters ? store.campaign.filters?.sortBy : 'name';
const sortOrder = store =>
  store.campaign.filters ? store.campaign.filters?.sortOrder : 'ASC';
const rowsPerPage = store =>
  store.campaign.filters ? store.campaign.filters?.rowsPerPage : 10;
const rowsPerPageFilter = store =>
  store.tableAndToolbar ? store.tableAndToolbar.paginatedFilters.rowsPerPage : 10;
const displayActive = store =>
  store.campaign.filters ? store.campaign.filters?.displayActive : true;
const filterTypes = store =>
  store.campaign.filters ? store.campaign.filters?.filterTypes : [];
const filterTemplates = store =>
  store.campaign.filters ? store.campaign.filters?.filterTemplates : [];
const filterProductNames = store =>
  store.campaign.filters ? store.campaign.filters?.filterProductNames : [];
const filterProductCategories = store =>
  store.campaign.filters ? store.campaign.filters?.filterProductCategories : [];
const filterOccurrenceTypes = store =>
  store.campaign.filters ? store.campaign.filters?.filterOccurrenceTypes : [];
const filterMarketingTypes = store =>
  store.campaign.filters ? store.campaign.filters?.filterMarketingTypes : [];
const totalCount = store =>
  store.campaign.filters ? store.campaign.filters?.totalCount : 0;
const totalCountFilter = store =>
  store.tableAndToolbar ? store.tableAndToolbar.paginatedFilters.totalCount : 0;
const activeFilter = store =>
  store.tableAndToolbar.paginatedFilters ? store.tableAndToolbar.paginatedFilters?.activeCampaignFilter : null;

// helper saga to prevent culling the saga tree on rejection of a parallelized task.
function* reflect(callArgs) {
  try {
    return { value: yield call(...callArgs), status: 'resolved' };
  } catch (ex) {
    return ex;
  }
}

export function* getPaginatedCampaignsIfNeeded(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let currentList = yield select(paginatedCampaignList);
    yield call(waitForGetSupportedEventTypes);
    let productCategory = yield select(productCategories);
    let productName = yield select(productNames);

    if (
      action.type === actions.GET_PAGINATED_CAMPAIGNS &&
      (currentList.length === 0 || payload.force === true)
    ) {
      const activeFilterValue = yield select(activeFilter);
      
      // Determine active filter value from payload for isFilter mode
      const getActiveValueFromPayload = () => {
        const { displayActive, displayArchived } = payload;
        // If only Active is selected, return true
        if (displayActive === true && displayArchived !== true) {
          return true;
        }
        // If only Archived is selected, return false
        if (displayArchived === true && displayActive !== true) {
          return false;
        }
        // If both or neither are selected, return null (don't filter)
        return null;
      };
      
      const payloadActiveValue = payload.isFilter ? getActiveValueFromPayload() : null;
      
      // Get searchScopes from payload for isFilter mode (hierarchy filter)
      const payloadSearchScopes = payload.isFilter && Array.isArray(payload.searchScopes) && payload.searchScopes.length > 0 
        ? payload.searchScopes 
        : null;
      
      let requestBody = payload.isFilter === true ?
      {
        name: yield select(searchInputFilter),
        sortBy: 'name',
        sortOrder: 'ASC',
        type: yield select(filterTypes),
        productName: yield select(filterProductNames),
        productCategory: yield select(filterProductCategories),
        ...(payloadActiveValue !== null ? { active: payloadActiveValue } : (activeFilterValue !== null && { active: activeFilterValue })),
        ...(payloadSearchScopes && { searchScopes: payloadSearchScopes })
      } :{
        name: yield select(searchInput),
        sortBy: yield select(sortBy),
        sortOrder: yield select(sortOrder),
        active: yield select(displayActive),
        type: yield select(filterTypes),
        searchScopes: yield select(filterTemplates),
        productName: yield select(filterProductNames),
        productCategory: yield select(filterProductCategories),
        campaignOccurrenceType: yield select(filterOccurrenceTypes),
        promotional: yield select(filterMarketingTypes)
      };
      let urlParams = payload.isFilter === true ?
      {
        index: yield select(pageNoFilter),
        pageSize: yield select(rowsPerPageFilter)
      } :
      {
        index: yield select(pageNo),
        pageSize: yield select(rowsPerPage)
      };
      let bodyParams = { ...getParams(requestBody, productName, productCategory) };
      if (payload.excludeListType) {
        bodyParams = {
          ...bodyParams,
          productName: payload.filteredProductName.length > 0 ? payload.filteredProductName : bodyParams.productName.filter(name => !excludeProductNames.includes(name)),
          productCategory: bodyParams.productCategory.filter(category => !excludeProductCategories.includes(category))
        }
      }
      let listCount = yield select(payload.isFilter === true ? totalCountFilter : totalCount);
      if (
        isArchiveInProgress &&
        listCount - 1 &&
        urlParams.index >= (listCount - 1) / urlParams.pageSize
      ) {
        urlParams.index = urlParams.index - 1;
        yield put(payload.isFilter === true ? updatePaginatedFilters({ pageNo: urlParams.index }) : updateCampaignFilters({ pageNo: urlParams.index }));
      }
      yield put(actions.gettingPaginatedCampaigns());
      currentList = yield call(
        getPaginatedCampaignsAsync,
        tenantId,
        businessContextId,
        urlParams,
        bodyParams
      );
      // Update totalCount in both stores - paginatedFilters for FilterBar and campaign.filters for MultiSelectDropdown
      if (payload.isFilter === true) {
        yield put(updatePaginatedFilters({ totalCount: currentList.totalRecords }));
        yield put(updateCampaignFilters({ totalCount: currentList.totalRecords }));
      } else {
        yield put(updateCampaignFilters({ totalCount: currentList.totalRecords }));
      }
    }
    isArchiveInProgress = false;
    yield put(actions.gettingPaginatedCampaignsSucceeded(currentList));
  } catch (ex) {
    isArchiveInProgress = false;
    yield put(updateCampaignFilters({ totalCount: 0 }));
    yield put(actions.gettingPaginatedCampaignsFailed(ex));
  }
}

export function* getAdvancedSearchPaginatedCampaignsIfNeeded(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let currentList = yield select(advancedPaginatedCampaignList);
    yield call(waitForGetSupportedEventTypes);
    let productCategory = yield select(productCategories);
    let productName = yield select(productNames);

    if (
      action.type === actions.GET_ADVANCED_SEARCH_PAGINATED_CAMPAIGNS &&
      (currentList.length === 0 || payload.force === true)
    ) {
      const activeFilterValue = payload.activeFilter !== undefined ? payload.activeFilter : (yield select(activeFilter));
      const searchNameValue = payload.searchName !== undefined ? payload.searchName : (yield select(searchInput));
      const promotionalFilterValue = payload.promotionalFilter !== undefined ? payload.promotionalFilter : (yield select(filterMarketingTypes));
      const selectedTypes = payload.type !== undefined ? payload.type : (yield select(filterTypes));
      const normalizedTypeFilter = Array.isArray(selectedTypes)
        ? selectedTypes
        : (selectedTypes ? [selectedTypes] : []);
      // Use searchScopes if explicitly provided (including null), otherwise auto-detect for Default context
      const searchScopesValue = ('searchScopes' in payload)
        ? payload.searchScopes 
        : (businessContextId === 'Default' ? ['tenantOnly'] : null);
      let requestBody = payload.isFilter === true ?
      {
        name: yield select(searchInputFilter),
        sortBy: 'name',
        sortOrder: 'ASC',
        type: normalizedTypeFilter,
        productName: yield select(filterProductNames),
        productCategory: yield select(filterProductCategories),
        ...(activeFilterValue !== null && { active: activeFilterValue })
      } :{
        name: searchNameValue,
        sortBy: yield select(sortBy),
        sortOrder: yield select(sortOrder),       
        type: normalizedTypeFilter,
        ...(searchScopesValue && { searchScopes: searchScopesValue }),
        productName: yield select(filterProductNames),
        productCategory: yield select(filterProductCategories),
        campaignOccurrenceType: yield select(filterOccurrenceTypes),
        ...(
          promotionalFilterValue !== null &&
          promotionalFilterValue !== undefined &&
          { promotional: promotionalFilterValue }
        ),
        requiredFields: ['id','uuid','tenantId','businessContextId','name','active','type','promotional','startDate','endDate','productName'],
        ...(activeFilterValue !== null && { active: activeFilterValue })
      };
      const defaultPageSize = payload.pageSize || parseInt(getEnvVar('ADM_DEFAULT_PAGE_SIZE')) || 999;
      let urlParams = payload.isFilter === true ?
      {
        index: yield select(pageNoFilter),
        pageSize: defaultPageSize
      } :
      {
        index: yield select(pageNo),
        pageSize: defaultPageSize
      };
      let bodyParams = { ...getParams(requestBody, productName, productCategory) };
      if (payload.excludeListType) {
        bodyParams = {
          ...bodyParams,
          productName: payload.filteredProductName.length > 0 ? payload.filteredProductName : bodyParams.productName.filter(name => !excludeProductNames.includes(name)),
          productCategory: bodyParams.productCategory.filter(category => !excludeProductCategories.includes(category))
        }
      }
      let listCount = yield select(payload.isFilter === true ? totalCountFilter : totalCount);
      if (
        isArchiveInProgress &&
        listCount - 1 &&
        urlParams.index >= (listCount - 1) / urlParams.pageSize
      ) {
        urlParams.index = urlParams.index - 1;
        yield put(payload.isFilter === true ? updatePaginatedFilters({ pageNo: urlParams.index }) : updateCampaignFilters({ pageNo: urlParams.index }));
      }
      yield put(actions.gettingPaginatedCampaigns());
      currentList = yield call(
        getAdvancedSearchPaginatedCampaignsAsync,
        tenantId,
        businessContextId,
        urlParams,
        bodyParams
      );
      yield put(payload.isFilter === true ? updatePaginatedFilters({ totalCount: currentList.totalRecords }) : updateCampaignFilters({ totalCount: currentList.totalRecords }));
    }
    isArchiveInProgress = false;
    yield put(actions.gettingAdvancedSearchPaginatedCampaignsSucceeded(currentList));
  } catch (ex) {
    isArchiveInProgress = false;
    yield put(updateCampaignFilters({ totalCount: 0 }));
    yield put(actions.gettingAdvancedSearchPaginatedCampaignsFailed(ex));
  }
}

export function* getCampaignsIfNeeded(action) {
  const { payload = {} } = action;

  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let currentList = yield select(campaignListSelector);

    yield put(actions.gettingCampaigns());

    if (currentList.length === 0 || payload.force === true) {
      ({ currentList } = yield all({
        template: yield call(waitForGetTemplates),
        attachmentTemplate: yield call(waitForGetAttachmentsTemplates),
        supportedEventTypes: yield call(waitForGetSupportedEventTypes),
        currentList: yield call(getCampaignsAsync, tenantId, businessContextId)
      }));
    }
    yield put(actions.gettingCampaignsSucceeded(currentList));
  } catch (ex) {
    yield put(actions.gettingCampaignsFailed(ex));
  }
}
function checkCampaignhasSetTemplate(campaign) {
  return campaign.uuid ||
    (campaign.hasOwnProperty('_hasSetTemplate') &&
      !campaign._hasSetTemplate)
}
export function* getCampaignIfNeeded(action) {
  const { payload } = action;

  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let campaign = yield select(currentCampaign);
    let campaignUuid = payload.campaignUuid;

    let yieldAlls = {
      supportedEventTypes: yield call(waitForGetSupportedEventTypes),
      campaign
    };

    yield put(actions.gettingCampaign());
    if (campaign.uuid !== campaignUuid || payload.force === true) {
      yieldAlls.campaign = call(
        getCampaignsAsync,
        tenantId,
        businessContextId,
        campaignUuid
      );
    }

    ({ campaign } = yield all(yieldAlls));

    let eventMetaData;
    let eventFields = [];
    if (campaign.schemaIdentifier || campaign.campaignOccurrenceType) {
      ({ eventMetaData } = yield call(waitForGetEventMetaData, {
        campaignOccurrenceType: campaign.campaignOccurrenceType,
        schemaIdentifier: campaign.schemaIdentifier,
        eventTypeCategory: campaign?.eventTypeCategory,
        productName: campaign.productName,
        workflow: 'campaign',
        productType: campaign.productType
      }));
      eventMetaData.map(data =>
        eventFields.push(...data.schemaConditionAttributes)
      );
      if (checkCampaignhasSetTemplate(campaign)) {
        campaign = mapTemplateRules(campaign, eventFields);
      }

      if (
        campaign.uuid ||
        (campaign.hasOwnProperty('_hasSetCampaignExclusionRules') &&
          !campaign._hasSetCampaignExclusionRules)
      ) {
        campaign = produce(campaign, draftCampaign => {
          draftCampaign.campaignExclusionConditionExpression = draftCampaign.campaignExclusionConditionExpression
            ? mapExpressionFromResposne(
              draftCampaign.campaignExclusionConditionExpression,
              eventFields
            )
            : {};
          draftCampaign._hasSetCampaignExclusionRules = true;
        });
      }
    }

    yield put(actions.gettingCampaignSucceeded({ campaign, eventMetaData }));
  } catch (ex) {
    yield put(actions.gettingCampaignFailed({ error: ex }));
  }
}

export function* createCampaignFromCurrent() {
  try {
    yield put(actions.creatingCampaign());
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let campaign = yield select(currentCampaign);

    campaign = produce(campaign, thisCampaign => {
      thisCampaign.communicationTemplateConditionQueries = flattenTemplateRulesForRequest(
        thisCampaign.templates
      );
      thisCampaign.campaignExclusionConditionExpression = flattenExpressionForRequest(
        thisCampaign?.campaignExclusionConditionExpression
      );
      thisCampaign.active = true;
    });

    if (!campaign.uuid) {
      campaign = yield call(
        createCampaignAsync,
        tenantId,
        businessContextId,
        campaign
      );
    }

    yield put(actions.creatingCampaignSucceeded({ campaign }));
  } catch (ex) {
    yield put(actions.creatingCampaignFailed(ex));
  }
}

export function* updateCampaignFromCurrent(action) {
  try {
    yield put(actions.updatingCampaign());
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let campaign = yield select(currentCampaign);
    let eventMetaData = campaign.eventMetaData;
    let campaignExclusionConditionExpression =
      campaign.campaignExclusionConditionExpression;
    let templates = campaign.templates;
    let hasSetCampaignExclusionRules = campaign._hasSetCampaignExclusionRules;

    campaign = produce(campaign, draftCampaign => {
      draftCampaign.communicationTemplateConditionQueries = flattenTemplateRulesForRequest(
        campaign.templates
      );
      draftCampaign.campaignExclusionConditionExpression = flattenExpressionForRequest(
        draftCampaign?.campaignExclusionConditionExpression
      );
      draftCampaign.startDate = convertToDateOnlyString(
        draftCampaign.startDate
      );
      draftCampaign.endDate = convertToDateOnlyString(draftCampaign.endDate);
    });

    if (campaign.uuid) {
      campaign = yield call(
        updateCampaignAsync,
        tenantId,
        businessContextId,
        campaign
      );
    }

    campaign = {
      ...campaign,
      eventMetaData: eventMetaData,
      templates: templates,
      campaignExclusionConditionExpression: campaignExclusionConditionExpression,
      _hasSetCampaignExclusionRules: hasSetCampaignExclusionRules
    };

    yield put(actions.updatingCampaignSucceeded(campaign));
  } catch (ex) {
    yield put(actions.updatingCampaignFailed(ex));
  }
}

export function* getAssociatedCampaignsFor(action) {
  const { payload } = action;

  let tenantId = yield select(currentTenantId);
  let businessContextId = yield select(currentBusinessContextId);

  let actionsMap = {
    [types.TEMPLATE]: {
      getting: TemplateActions.gettingAssociatedCampaignsForTemplate,
      succeeded: TemplateActions.gettingAssociatedCampaignsForTemplateSucceeded,
      failed: TemplateActions.gettingAssociatedCampaignsForTemplateFailed
    },
    [types.ATTACHMENT_TEMPLATE]: {
      getting:
        AttachmentTemplatesActions.gettingAssociatedCampaignsForAttachmentTemplate,
      succeeded:
        AttachmentTemplatesActions.gettingAssociatedCampaignsForAttachmentTemplateSucceeded,
      failed:
        AttachmentTemplatesActions.gettingAssociatedCampaignsForAttachmentTemplateFailed
    },
    [types.QUERY_TEMPLATE]: {
      getting:
        RecipientQueryTemplatesActions.gettingAssociatedCampaignsForQueryTemplate,
      succeeded:
        RecipientQueryTemplatesActions.gettingAssociatedCampaignsForQueryTemplateSucceeded,
      failed:
        RecipientQueryTemplatesActions.gettingAssociatedCampaignsForQueryTemplateFailed
    }
  };

  const chosenActions = actionsMap[payload.typeOf];

  if (!chosenActions) {
    return;
  }

  yield put(chosenActions.getting());

  try {
    let campaignList = yield call(
      getAssociatedCampaignsAsync,
      tenantId,
      businessContextId,
      payload.uuid,
      payload.typeOf
    );
    yield put(chosenActions.succeeded({ uuid: payload.uuid, campaignList }));
  } catch (ex) {
    yield put(chosenActions.failed(ex));
  }
}

export function* deleteCampaignsByIdList(action) {
  const { payload } = action;

  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let campaignUuids = payload.campaignUuids;
    let campaigns = yield select(paginatedCampaignList);
    let deletedCampaignStatuses = [];
    if (campaignUuids.length > 0) {
      yield put(actions.deletingCampaigns());
      // all() prallelizes delete operations and waits until they all finish.
      // if the delete campaign API method can be made to return the deleted id this could be set to a variable and report  tial success which would make for better failure handling.
      if (payload.hardDelete) {
        deletedCampaignStatuses = yield all(
          campaignUuids
            .map(campaignUuid => [
              deleteCampaignAsync,
              tenantId,
              businessContextId,
              campaignUuid
            ])
            .map(reflect)
        );
      } else {
        deletedCampaignStatuses = yield all(
          campaignUuids
            .map(campaignUuid => [
              updateCampaignAsync,
              tenantId,
              businessContextId,
              {
                ...campaigns.find(c => c.uuid === campaignUuid),
                active: !payload.activeStatus
              }
            ])
            .map(reflect)
        );
      }
    }
    if (
      deletedCampaignStatuses.filter(status => status.status === 'resolved')
        .length === campaignUuids.length
    ) {
      isArchiveInProgress = true;
      yield all([
        actions.deletingCampaignsSucceeded(campaignUuids, payload.hardDelete),
        put(actions.getPaginatedCampaigns({ force: true }))
      ]);
    } else {
      yield put(
        actions.deletingCampaignsFailed({
          message: 'Not all requested campaigns could be deleted.',
          deletedCampaignStatuses
        })
      );
    }
  } catch (ex) {
    isArchiveInProgress = false;
    yield put(
      actions.deletingCampaignsFailed({
        ...ex,
        hardDelete: payload.hardDelete
      })
    );
  }
}

export function* getAllActiveCampaignsIfNeeded(action) {
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let allCampaignsList = yield call(
      getAllActiveCampaignsAsync,
      tenantId,
      businessContextId
    );
    yield put(actions.getAllActiveCampaignsSucceeded(allCampaignsList));
  } catch (ex) {
    yield put(actions.getAllActiveCampaignsFailed(ex));
  }
}